import { useEffect, useState } from 'react';
import WorldMap from "react-svg-worldmap";
import { Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { countries, TCountryCode } from 'countries-list';

import { getCurrentMonth } from '@/utils/date';
import { getInspectionsByCountry, InspectionByCountry } from '@/services/dashboardService';

import MonthSelector from './MonthSelector';
import './dashboard.css';
import { DataStatus } from './dataStatus';
import { LoadingSpinner } from './LoadingSpinner';
import chartColors from './chartColors';
import useSmallScreen from '@/utils/UseDeviceSize';

type CountryData = {
    country: TCountryCode, // refer to ISO 3166-1 alpha-2, but use lowercase letters
    value: number,
};

const getCountryColor = (value: number) => {
    if (value >= 1 && value <= 30) return '#FFD600'; // Yellow
    if (value >= 31 && value <= 50) return '#10B981'; // Green
    if (value > 50) return '#EF4444'; // Red
    return '#E5E7EB'; // Default gray for 0
};

const ChartInspectionsByCountry = () => {
    const {year: currentYear, month: currentMonth} = getCurrentMonth();
    const isSmallScreen = useSmallScreen();

    const [data, setData] = useState<CountryData[]>([]);
    const [dataStatus, setDataStatus] = useState<DataStatus>(DataStatus.LOADING);
    const [month, setMonth] = useState<number>(currentMonth);
    const [year, setYear] = useState<number>(currentYear);

    const transformData = (originalData: InspectionByCountry[]) => {
        const countryData: CountryData[] = [];
        originalData.map((country) => {
            countryData.push({
                country: country.country,
                value: country.totalInspection,
            });
        });
        countryData.sort((a, b) => (b.value - a.value));
        return countryData;
    };
    
    const getData = async (month: number, year: number) => {
        setDataStatus(DataStatus.LOADING)
        const response = await getInspectionsByCountry(month, year);
        if (!response.data){
            setDataStatus(DataStatus.ERROR);
            return;
        }
        if (response.data.length === 0){
            setDataStatus(DataStatus.EMPTY);
            return;
        }
        const originalData = response.data as InspectionByCountry[];
        setData(transformData(originalData))
        setDataStatus(DataStatus.OK);
    };

    useEffect(() => {
        getData(currentMonth, currentYear);
    }, []);

    useEffect(() => {
        getData(month, year);
    }, [month, year]);

    return (
        <div className='card-full'>
            <div>
                <h2 className='card-title'>Inspections by Country</h2>
                <MonthSelector month={month} year={year} onChange={(selectedMonth, selectedYear) => { setMonth(selectedMonth); setYear(selectedYear) }} />
                {(dataStatus === DataStatus.LOADING) && <LoadingSpinner />}
            </div>
            
            {(dataStatus === DataStatus.EMPTY || dataStatus === DataStatus.ERROR) && <div>No data to display</div>}
            {(dataStatus === DataStatus.OK || dataStatus === DataStatus.LOADING) && <>
                <div className='chart-with-table-chart' style={{ borderRadius: '8px', overflow: 'hidden' }}>
                    <WorldMap
                        color={chartColors.darkred}
                        value-suffix="inspections"
                        data={data}
                        size={isSmallScreen ? 'responsive' : Math.min(window.innerWidth * 0.5, 700)}
                        styleFunction={(context) => {
                            const value = context.countryValue || 0;
                            return {
                                fill: getCountryColor(value),
                                stroke: '#fff',
                                strokeWidth: 1,
                            };
                        }}
                    />
                </div>
                <div className='chart-with-table-table'>
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>Country</Th>
                                <Th>Inspections</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {data.map((countryData) => (
                                <Tr key={countryData.country}>
                                    <Td>{countries[countryData.country].name}</Td>
                                    <Td textAlign='right' style={{ color: getCountryColor(countryData.value), fontWeight: 'bold' }}>{countryData.value}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </div>
            </>}
            
        </div>
    );
};

export default ChartInspectionsByCountry;
